import { useCallback, useEffect, useState } from 'react';
import { getCsrfToken } from '../../utils/csrf';
import { ApiAssetData, AssetTotalDash } from '../models/ModelInvestments';

export const useInvestmentDashboard = () => {
  const [assetTotals, setAssetTotals] = useState<AssetTotalDash[]>([])
  const [showUSD, setShowUSD] = useState(false)
  const [exchangeRate, setExchangeRate] = useState<number>(5)
  const [isLoading, setIsLoading] = useState(true)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [isRecalculating, setIsRecalculating] = useState(false)

  const fetchDashboardData = useCallback(async () => {
    // Add a timeout to the fetch operation
    const fetchWithTimeout = async (url: string, options: RequestInit, timeout = 10000) => {
      const controller = new AbortController();
      const id = setTimeout(() => controller.abort(), timeout);
      
      try {
        const response = await fetch(url, {
          ...options,
          signal: controller.signal
        });
        clearTimeout(id);
        return response;
      } catch (error) {
        clearTimeout(id);
        throw error;
      }
    };
    
    try {
      // Always set loading to true on initial load
      if (isInitialLoad) {
        setIsLoading(true)
      }

      const csrfToken = await getCsrfToken()
      
      const response = await fetchWithTimeout('/api/investments/dashboard/', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
      }, 15000) // 15 second timeout

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)

      const data = await response.json()
      
      if (data.success) {
        if (!data.data.asset_totals || !Array.isArray(data.data.asset_totals)) {
          console.error('Invalid asset_totals data:', data.data.asset_totals)
          setAssetTotals([])
        } else {
          const mappedAssets = data.data.asset_totals.map((asset: ApiAssetData) => {
            return {
              asset_type: asset.asset_type || 'unknown',
              currency: asset.currency || 'unknown',
              total_value: asset.total_invested_amount || '0',
              total_current_value: asset.total_current_value || '0',
              total_dividends: asset.total_dividends || '0',
              total_invested_amount: asset.total_invested_amount || '0',
              total_profit: asset.total_profit || '0'
            }
          })
          
          setAssetTotals(mappedAssets)
          
          if (data.data.exchange_rate) {
            const rate = parseFloat(data.data.exchange_rate)
            setExchangeRate(isNaN(rate) ? 5 : rate)
          }
          
          // Update recalculating status
          setIsRecalculating(!!data.data.is_recalculating)
        }
      } else {
        console.error('API returned unsuccessful response:', data)
        setAssetTotals([])
        setIsRecalculating(false)
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
      // Set empty data to allow UI to show empty state
      setAssetTotals([])
      setIsRecalculating(false)
      
      // If it's an AbortError (timeout), provide a specific message
      if (error instanceof DOMException && error.name === 'AbortError') {
        console.error('Request timed out. The server might be unresponsive.');
      }
    } finally {
      // Always set loading to false after fetch completes
      setIsLoading(false)
      if (isInitialLoad) {
        setIsInitialLoad(false)
      }
    }
  }, [isInitialLoad])

  // Add a safety timeout to ensure loading state doesn't get stuck
  useEffect(() => {
    if (isLoading) {
      const safetyTimeout = setTimeout(() => {
        setIsLoading(false);
        setIsInitialLoad(false);
      }, 20000); // 20 seconds safety timeout
      
      return () => clearTimeout(safetyTimeout);
    }
  }, [isLoading]);

  useEffect(() => {
    fetchDashboardData()
    
    const interval = setInterval(fetchDashboardData, 30000)
    return () => clearInterval(interval)
  }, [fetchDashboardData])

  return {
    assetTotals,
    showUSD,
    setShowUSD,
    exchangeRate,
    isLoading,
    isRecalculating,
    fetchDashboardData
  }
}